import { MenuItem, Select, styled } from "@material-ui/core";
import { useIntl } from "react-intl";
import {
  CenteredInputBox,
  CenteredReadOnlyValue,
  PRICE_LIST_CELL_INPUT_CLASS,
} from "./inline-cell-styles";
import type { RevenueDepartmentOption } from "./use-pending-price-edits";

const FullWidthSelect = styled(Select)({
  width: "100%",
  "& .MuiSelect-select": {
    textAlign: "center",
  },
});

type Props = {
  rowId: string;
  selectedId: string;
  selectedName: string;
  options: readonly RevenueDepartmentOption[];
  onChange: (next: RevenueDepartmentOption) => void;
  disabled?: boolean;
};

export const InlineRevenueDepartmentCell: FC<Props> = ({
  rowId,
  selectedId,
  selectedName,
  options,
  onChange,
  disabled,
}) => {
  const { formatMessage } = useIntl();

  if (disabled) {
    return (
      <CenteredReadOnlyValue
        data-testid={`priceList.row.${rowId}.revenueDepartment`}
      >
        {selectedName}
      </CenteredReadOnlyValue>
    );
  }

  // Local fallback option keeps the Select showing the current selection
  // even if the shared options haven't been fetched yet (race on first paint).
  const mergedOptions: readonly RevenueDepartmentOption[] = options.length
    ? options
    : [{ id: selectedId, name: selectedName }];

  return (
    <CenteredInputBox onClick={(e) => e.stopPropagation()}>
      <FullWidthSelect
        variant="outlined"
        margin="dense"
        IconComponent={() => null}
        value={selectedId}
        onChange={(e) => {
          const nextId = e.target.value as string;
          if (nextId === selectedId) return;
          const next = mergedOptions.find((o) => o.id === nextId);
          if (next) onChange(next);
        }}
        onClick={(e) => e.stopPropagation()}
        inputProps={{
          "aria-label": formatMessage({
            id: "priceList.inlineRevenueDepartment.trigger",
            defaultMessage: "Edit revenue department",
          }),
          "data-testid": `priceList.row.${rowId}.revenueDepartment`,
          className: PRICE_LIST_CELL_INPUT_CLASS,
        }}
      >
        {mergedOptions.map((option) => (
          <MenuItem key={option.id} value={option.id}>
            {option.name}
          </MenuItem>
        ))}
      </FullWidthSelect>
    </CenteredInputBox>
  );
};
